import { HTMLAttributes, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { useRegionStore } from '@packages/stores';
import { TripCostForm } from '@packages/types';
import { convertCentsToDollars } from '@packages/utils';

import { TotalPriceButton } from '@components';
import { AfterPayBanner } from '@components/afterPayBanner';
import { Button } from '@elements/button';
import { Text } from '@elements/text';
import { useEmailQuote } from '@hooks';
import { i18n } from '@i18n';
import { ROUTE_PATHS, getCurrentPathname, router } from '@routes/router';
import {
  top3BoostHasBeenAddedSelector,
  useFeatureFlagsStore,
  useModalStore,
  useTripStore,
} from '@store';
import { testProps } from '@utils';

type TotalPriceAndGroupDiscountProps = HTMLAttributes<HTMLDivElement> & {
  showBuyNow?: boolean;
  showAfterPay?: boolean;
};

export const TotalPriceAndGroupDiscount = ({
  showBuyNow = false,
  showAfterPay = false,
  className,
  ...rest
}: TotalPriceAndGroupDiscountProps) => {
  const totalPrice = useTripStore(state => state?.trip?.price);

  return (
    <div className={twMerge('flex gap-4 flex-col py-5', className)} {...rest}>
      <TotalPrice amount={totalPrice} />
      {showAfterPay && <AfterPayBanner className="pt-1" price={totalPrice} />}
      {showBuyNow ? <BuyNowButtonSection className="hidden sm:block" /> : null}
    </div>
  );
};

const TotalPrice = ({ amount }: { amount: number }) => {
  return (
    <div className="flex items-center justify-between py-5 border-y">
      <Text variant="h3-28/sb">{i18n.t('checkout.sideBar.payment.title')}</Text>
      <div className="flex">
        <Text variant="body-16/sb" {...testProps('total-price-currency-sign')}>
          {i18n.t('checkout.sideBar.payment.currencySign')}
        </Text>
        <Text variant="h3-28/sb" className="pr-2" {...testProps('total-price')}>
          {convertCentsToDollars(amount).replace('$', '')}
        </Text>
      </div>
    </div>
  );
};

export const BuyNowButtonSection = (props: HTMLAttributes<HTMLDivElement>) => {
  const { checkout } = ROUTE_PATHS();
  const currentPathname = getCurrentPathname();

  const emailQuote = useEmailQuote();
  const isEmailQuoteEnabled = useFeatureFlagsStore(
    state => state.featureFlags?.['email-quote']?.isEnabled,
  );

  const button = useMemo(() => {
    if (currentPathname === checkout) {
      return <CheckoutAction />;
    }
    return <MyQuoteAction />;
  }, [checkout, currentPathname]);

  return (
    <div
      {...props}
      className={twMerge(
        'flex-row gap-2 py-3 lg:py-0 sm:flex-row-reverse md:flex-row lg:flex',
        props.className,
      )}>
      {button}
      {isEmailQuoteEnabled && (
        <Button
          {...testProps('email_quote_button')}
          variant="secondary"
          className="w-full sm:w-[150px] lg:w-full"
          onClick={() => {
            emailQuote?.openEmailQuoteModal();
          }}
          title={i18n.t('checkout.text_one_pager_email_quote_btn')}
        />
      )}
    </div>
  );
};

function CheckoutAction() {
  return (
    <div className="w-full sm:max-w-[250px] sm:min-w-[150px] md:max-w-full items-center justify-center flex ">
      <TotalPriceButton />
    </div>
  );
}

function MyQuoteAction() {
  const [isLoading, setIsLoading] = useState(false);
  const isPending = useTripStore(state => state?.pending);
  const tripCost = useTripStore(state => state?.trip?.price);
  const { getValues, setError, trigger } = useFormContext<TripCostForm>();
  const isUS = useRegionStore(state => state?.region?.country === 'US');
  const isBoostUpdating = useTripStore(state =>
    Object.values(state?.boostApiState)?.some(i => i === 'pending'),
  );
  const openModal = useModalStore(state => state.openModal);

  const onClick = useCallback(async () => {
    const isValid = await trigger('totalTripCost');
    if (!isValid && isUS) {
      setError('totalTripCost', {
        type: 'manual',
        message: 'Please update trip cost to continue.',
      });
      return;
    }
    setIsLoading(true);
    const currentFormTripCost = parseInt(getValues('totalTripCost')?.replace(/[^0-9]/g, ''));

    if (isUS && currentFormTripCost !== useTripStore.getState().trip?.totalTripCost) {
      setError('totalTripCost', {
        type: 'manual',
        message: 'Please update trip cost to continue.',
      });
      setIsLoading(false);
      return;
    }
    const hasTop3BoostsAdded = top3BoostHasBeenAddedSelector(useTripStore.getState());

    setIsLoading(false);
    if (!isUS && !hasTop3BoostsAdded) {
      openModal('Modal', {
        body: {
          type: 'PersonalisationBoostModal',
        },
        size: 'lg',
        shouldCloseOnOverlayClick: true,
        showCloseButton: false,
      });
    } else {
      router.navigate(ROUTE_PATHS().checkout);
    }
  }, [getValues, isUS, openModal, setError, trigger]);

  return (
    <>
      <Button
        isLoading={isLoading}
        {...testProps('text_buy_now_button')}
        variant="primary"
        className="w-full hidden sm:block"
        onClick={onClick}
        title="Continue"
        disabled={isBoostUpdating || isPending}
      />
      <div className="flex items-center justify-between sm:hidden">
        <div className="flex items-center gap-1">
          <Text variant="h4-24/sb" className="self-start">
            {i18n.t('checkout.sideBar.payment.currencySign')}
          </Text>
          <Text variant="h2-36/sb">{convertCentsToDollars(tripCost).replace('$', '')}</Text>
        </div>
        <Button
          isLoading={isLoading}
          {...testProps('text_buy_now_button')}
          variant="primary"
          onClick={onClick}
          title="Continue"
          disabled={isBoostUpdating}
        />
      </div>
    </>
  );
}
